import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";

import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const LipidStocksContext = createContext();

export default LipidStocksContext;

export const LipidStocksProvider = ({ children }) => {
  const api = useAxios();
  const navigate = useNavigate();

  const initValues = {
    today: dayjs(Date.now()),
    solventArray: [],
    operatorArray: [],
    lipidAliquotsArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const lipidStocksObj = {
    lipdStockId: '',
    solvent: null,
    concentration: '',
    datePrepared: null,
    operatorArray: [],
    amountPrepared: '',
    lipidAliquotsArray: [],
  };
  const [lipidStocksData, setLipidStocksData] = useState(lipidStocksObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [lipidStocksId, setLipidStocksId] = useState('');

  useEffect(() => {
    getInitialData();
    getOperators();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/lipid-stocks/initial/data/get'
      );
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          lipidAliquotsArray: response.data.lipid_aliquots_list,
          solventArray: response.data.solvent_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Stock Init Data Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {        
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (lipidStocksData?.lipidAliquotsArray.length === 0) {
      errors['lipidAliquots'] = 'Lipid Aliquots is required!';
    }
    if (!lipidStocksData?.lipdStockId) {
      errors['lipdStockId'] = 'Unique lipid stock ID is required!';
    }
    if (!lipidStocksData?.solvent) {
      errors['solvent'] = 'Solvent is required!';
    }
    if (!lipidStocksData?.datePrepared) {
      errors['datePrepared'] = 'Date prepared is required!';
    }
    if (!lipidStocksData?.amountPrepared) {
      errors['amountPrepared'] = 'Amount prepared (mL) is required!';
    }

    if (!lipidStocksData?.concentration) {
      errors['concentration'] = 'Concentration is required!';
    }
    if (lipidStocksData?.operatorArray.length === 0) {
      errors['operator'] = 'Operator is required!';
    }

    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      submitLipidStocks();
    }
  }, [formErrors]);

  const submitLipidStocks = () => {
    if (lipidStocksId === '') {
      createLipidStocks();
    } else {
      updateLipidStocks();
    }
  };

  const createLipidStocks = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post(
        '/api/materials-capture/lipid-stocks/create', {
        unique_lipid_stock_id: lipidStocksData?.lipdStockId,
        solvent: lipidStocksData?.solvent,
        concentration: lipidStocksData?.concentration,
        date_prepared: lipidStocksData?.datePrepared,
        operator_list: lipidStocksData?.operatorArray,
        amount_prepared: lipidStocksData?.amountPrepared,
        lipid_aliquots_list: lipidStocksData?.lipidAliquotsArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        navigate('/lipid-stocks');
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Stock create',
        loading: false,
      });
    }
  };

  const updateLipidStocks = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/lipid-stocks/edit', {
        lipid_stock_id: lipidStocksId,
        unique_lipid_stock_id: lipidStocksData?.lipdStockId,
        solvent: lipidStocksData?.solvent,
        concentration: lipidStocksData?.concentration,
        date_prepared: lipidStocksData?.datePrepared,
        operator_list: lipidStocksData?.operatorArray,
        amount_prepared: lipidStocksData?.amountPrepared,
        lipid_aliquots_list: lipidStocksData?.lipidAliquotsArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Stocks update',
        loading: false,
      });
    }
  };

  useEffect(() => {
    if (lipidStocksId !== '') getLipidStocksDetails()
  }, [lipidStocksId]);

  const getLipidStocksDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/lipid-stocks/details/get', {
          params: {
            lipid_stocks_id: lipidStocksId
          }
        });
      
      if (response.status === 200) {
        const lipidStocksData = response.data;          
        setLipidStocksData(prev => ({
          ...prev,
          lipdStockId: lipidStocksData.unique_lipid_stock_id,
          solvent: lipidStocksData.solvent,
          concentration: lipidStocksData.concentration,
          datePrepared: lipidStocksData.date_prepared,
          operatorArray: lipidStocksData.operator_list,
          amountPrepared: lipidStocksData.amount_prepared,
          lipidAliquotsArray: lipidStocksData.aliquot_id_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Stock Details Get',
        loading: false,
      });
    }
  };

  const contextData = {
    initialValues: initialValues,
    lipidStocksData: lipidStocksData,
    setLipidStocksData: setLipidStocksData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    setLipidStocksId: setLipidStocksId,
  };

  return (
    <LipidStocksContext.Provider value={contextData}>
      {children}
    </LipidStocksContext.Provider>
  )
};